import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  SelectData,
  SelectWithSearch,
  Tabs,
  TabsList,
  TabsTrigger,
  useStepperReturn,
} from '@ais-ui/ui-core';
import {
  AutoCompleteShipInfo,
  Filter,
  useGetFilters,
} from '@ais-ui/websockets';

import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { StepperFooter } from './StepperFooter';
import {
  FilterStructure,
  FilterType,
  ReportFilterItem,
} from '../../reportsTypes';
import { useFetchAllShiplists } from '@ais-ui/api';
import { ShipList } from '@ais-ui/api-spec/config';
import { ReportFilterType } from '../../utils';
import { MultiShipCommand } from '../../../utils/ShipCommand';
import { StepperHeadline } from './StepperHeadline';
import { shipSchema } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { useCreateReportContext } from './CreateReportContext';
import * as Sentry from '@sentry/react';

const reportTemplateFilterFormSchema = z.object({
  filterId: z.string().optional(),
});

type reportTemplateFilterValues = z.infer<
  typeof reportTemplateFilterFormSchema
>;

const reportTemplateShipsFilterFormSchema = z.object({
  ships: z.array(shipSchema).nonempty(),
});

type reportTemplateShipsFilterValues = z.infer<
  typeof reportTemplateShipsFilterFormSchema
>;

const reportTemplateShiplistFilterFormSchema = z.object({
  shiplist: z.string().nonempty(),
});

type reportTemplateShiplistFilterValues = z.infer<
  typeof reportTemplateShiplistFilterFormSchema
>;

const reportTemplateCombinedFilterFormSchema = z.object({
  shiplist: z.string().optional(),
  filterId: z.string().optional(),
});

type reportTemplateCombinedFilterValues = z.infer<
  typeof reportTemplateCombinedFilterFormSchema
>;

interface NormalFilterProps {
  filterItems: ReportFilterItem[];
  filter: Filter | null;
  setFilter: (filter: Filter | null) => void;
  stepperProps: useStepperReturn;
}

const NormalFilterForm = ({
  filterItems,
  filter,
  setFilter,
  stepperProps,
}: NormalFilterProps) => {
  const { messages } = useLocalization();
  const { data: filters } = useGetFilters();

  const form = useForm<reportTemplateFilterValues>({
    resolver: zodResolver(reportTemplateFilterFormSchema),
    defaultValues: {
      filterId: filter ? filter.uuid : 'no-filter',
    },
  });

  const selections = useMemo(() => {
    const selectionItems = filters
      ? filters.map((filter) => ({
          id: filter.uuid,
          name: filter.name,
        }))
      : [];
    selectionItems.unshift({ id: 'no-filter', name: messages.reportsCreateReportNoFilterValue ?? 'No filter' });
    return selectionItems;
  }, [filters, messages]);

  const handleFilterNotFound = useCallback((filterId: string) => {
    Sentry.captureMessage('Filter not found in CreateReport', {
      level: 'warning',
      tags: { component: 'NormalFilterForm', context: 'filter-not-found' },
      extra: { missingFilterId: filterId },
    });
  }, []);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = (data: reportTemplateFilterValues) => {
    if (filters) {
      for (const filter of filters) {
        if (filter.uuid === data.filterId) {
          setFilter(filter);
          break;
        }
      }
    }

    if (data.filterId === 'no-filter' || !data.filterId) {
      setFilter(null);
    }

    stepperProps.nextStep();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-full flex-grow"
      >
        <div className="h-[85%]">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="filterId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Filter:</FormLabel>
                  <FormControl onChange={() => form.clearErrors()}>
                    <SelectWithSearch
                      value={field.value}
                      onChange={field.onChange}
                      data={selections}
                      placeholder={
                        messages.reportsCreateReportSelectFilterPlaceholder
                      }
                      notFoundText={messages.filterNotFound || 'Filter not found'}
                      onNotFound={handleFilterNotFound}
                    ></SelectWithSearch>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

interface ShipFilterProps {
  filterItems: ReportFilterItem[];
  filter: AutoCompleteShipInfo[] | null;
  setFilter: (filter: AutoCompleteShipInfo[] | null) => void;
  stepperProps: useStepperReturn;
}

const ShipFilterForm = ({
  filterItems,
  filter,
  setFilter,
  stepperProps,
}: ShipFilterProps) => {
  const form = useForm<reportTemplateShipsFilterValues>({
    resolver: zodResolver(reportTemplateShipsFilterFormSchema),
    defaultValues: {
      ships: filter ? filter : [],
    },
  });

  // Reset form when filter context changes (e.g., when switching from Quick to Wizard)
  useEffect(() => {
    const currentFormShips = form.getValues('ships');
    const contextShips = filter || [];

    // Only reset if the values are actually different to avoid infinite loops
    const areEqual =
      currentFormShips.length === contextShips.length &&
      currentFormShips.every((ship, index) =>
        ship.mmsi === contextShips[index]?.mmsi
      );

    if (!areEqual) {
      form.reset({ ships: contextShips });
    }
  }, [filter, form]);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = (data: reportTemplateShipsFilterValues) => {
    setFilter(data.ships);
    stepperProps.nextStep();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-full flex-grow"
      >
        <div className="h-[85%]">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="ships"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Filter:</FormLabel>
                  <FormControl onChange={() => form.clearErrors()}>
                    <MultiShipCommand
                      value={field.value}
                      onChange={field.onChange}
                    ></MultiShipCommand>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

interface ShiplistFilterProps {
  filterItems: ReportFilterItem[];
  filter: string | null;
  setFilter: (filter: string | null) => void;
  stepperProps: useStepperReturn;
}

interface CombinedFilterProps {
  filterItems: ReportFilterItem[];
  filter: Record<string, Filter | AutoCompleteShipInfo[] | string | null>;
  setFilter: (
    filter: Record<string, Filter | AutoCompleteShipInfo[] | string | null>
  ) => void;
  stepperProps: useStepperReturn;
}

const ShiplistFilterForm = ({
  filterItems,
  filter,
  setFilter,
  stepperProps,
}: ShiplistFilterProps) => {
  const { messages } = useLocalization();
  const { data: shiplists } = useFetchAllShiplists();

  const form = useForm<reportTemplateShiplistFilterValues>({
    resolver: zodResolver(reportTemplateShiplistFilterFormSchema),
    defaultValues: {
      shiplist: filter ? filter : '',
    },
  });

  const selections = useMemo(() => {
    const selectionItems: SelectData[] =
      shiplists && shiplists.records
        ? shiplists.records.map((shiplist: ShipList) => ({
            id: shiplist.id ? shiplist.id : '',
            name: shiplist.name ? shiplist.name : '',
          }))
        : [];
    selectionItems.unshift({
      id: 'no-filter',
      name: messages.reportsCreateReportNoFilterValue,
    });
    return selectionItems;
  }, [shiplists]);

  const handleShiplistNotFound = useCallback((shiplistId: string) => {
    Sentry.captureMessage('Shiplist not found in CreateReport', {
      level: 'warning',
      tags: { component: 'ShiplistFilterForm', context: 'shiplist-not-found' },
      extra: { missingShiplistId: shiplistId },
    });
  }, []);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = (data: reportTemplateShiplistFilterValues) => {
    setFilter(data.shiplist);
    stepperProps.nextStep();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-full flex-grow"
      >
        <div className="h-[85%]">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="shiplist"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Filter:</FormLabel>
                  <FormControl onChange={() => form.clearErrors()}>
                    <SelectWithSearch
                      value={field.value}
                      onChange={field.onChange}
                      data={selections}
                      placeholder={
                        messages.reportsCreateReportSelectShiplistPlaceholder
                      }
                      notFoundText={messages.shiplistNotFound || 'Shiplist not found'}
                      onNotFound={handleShiplistNotFound}
                    ></SelectWithSearch>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

const FilterLabel = ({ filterItem }: { filterItem: ReportFilterItem }) => {
  return (
    <div className="flex justify-between">
      <FormLabel>
        {filterItem.label}: {filterItem.required && '*'}
      </FormLabel>
      {filterItem.help && (
        <InfoIconTooltip className="mr-2">{filterItem.help}</InfoIconTooltip>
      )}
    </div>
  );
};

const CombinedFilterForm = ({
  filterItems,
  filter,
  setFilter,
  stepperProps,
}: CombinedFilterProps) => {
  const { data: filters } = useGetFilters();
  const { messages } = useLocalization();
  const { data: shiplists } = useFetchAllShiplists();

  const form = useForm<reportTemplateCombinedFilterValues>({
    resolver: zodResolver(reportTemplateCombinedFilterFormSchema),
    defaultValues: {
      shiplist: filter?.shiplist ? (filter.shiplist as string) : '',
      filterId: filter?.filter ? (filter.uuid as string) : 'no-filter',
    },
  });

  const shiplistSelections = useMemo(() => {
    const selectionItems: SelectData[] =
      shiplists && shiplists.records
        ? shiplists.records.map((shiplist: ShipList) => ({
            id: shiplist.id ? shiplist.id : '',
            name: shiplist.name ? shiplist.name : '',
          }))
        : [];
    selectionItems.unshift({
      id: 'no-filter',
      name: messages.reportsCreateReportNoFilterValue,
    });
    return selectionItems;
  }, [shiplists]);

  const filterSelections = useMemo(() => {
    const selectionItems = filters
      ? filters.map((filter) => ({
          id: filter.uuid,
          name: filter.name,
        }))
      : [];
    selectionItems.unshift({ id: 'no-filter', name: messages.reportsCreateReportNoFilterValue ?? 'No filter' });
    return selectionItems;
  }, [filters, messages]);

  const handleFilterNotFound = useCallback((filterId: string) => {
    Sentry.captureMessage('Filter not found in CreateReport CombinedFilter', {
      level: 'warning',
      tags: { component: 'CombinedFilterForm', context: 'filter-not-found' },
      extra: { missingFilterId: filterId },
    });
  }, []);

  const handleShiplistNotFound = useCallback((shiplistId: string) => {
    Sentry.captureMessage('Shiplist not found in CreateReport CombinedFilter', {
      level: 'warning',
      tags: { component: 'CombinedFilterForm', context: 'shiplist-not-found' },
      extra: { missingShiplistId: shiplistId },
    });
  }, []);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = (data: reportTemplateCombinedFilterValues) => {
    let newFilter: Filter | null = null;
    if (filters) {
      for (const filter of filters) {
        if (filter.uuid === data.filterId) {
          newFilter = filter;
          break;
        }
      }
    }

    setFilter({ shiplist: data.shiplist as string, filter: newFilter });

    stepperProps.nextStep();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-full flex-grow"
      >
        <div className="h-[85%]">
          <div className="space-y-6">
            {filterItems.map((filterItem) => {
              if (filterItem.name === 'shiplist') {
                return (
                  <FormField
                    key="shiplist"
                    control={form.control}
                    name="shiplist"
                    render={({ field }) => (
                      <FormItem>
                        <FilterLabel filterItem={filterItem}></FilterLabel>
                        <FormControl onChange={() => form.clearErrors()}>
                          <SelectWithSearch
                            value={field.value}
                            onChange={field.onChange}
                            data={shiplistSelections}
                            placeholder={
                              messages.reportsCreateReportSelectShiplistPlaceholder
                            }
                            notFoundText={messages.shiplistNotFound || 'Shiplist not found'}
                            onNotFound={handleShiplistNotFound}
                          ></SelectWithSearch>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                );
              }
              if (filterItem.name === 'filter') {
                return (
                  <FormField
                    key="filter"
                    control={form.control}
                    name="filterId"
                    render={({ field }) => (
                      <FormItem>
                        <FilterLabel filterItem={filterItem}></FilterLabel>
                        <FormControl onChange={() => form.clearErrors()}>
                          <SelectWithSearch
                            value={field.value}
                            onChange={field.onChange}
                            data={filterSelections}
                            placeholder={
                              messages.reportsCreateReportSelectFilterPlaceholder
                            }
                            notFoundText={messages.filterNotFound || 'Filter not found'}
                            onNotFound={handleFilterNotFound}
                          ></SelectWithSearch>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                );
              }
            })}
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

interface SingleContentProps {
  filterFields: FilterStructure;
  filter: ReportFilterType | null;
  setFilter: (filter: ReportFilterType | null) => void;
  stepperProps: useStepperReturn;
}

const SingleContent = ({
  filterFields,
  filter,
  setFilter,
  stepperProps,
}: SingleContentProps) => {
  if (
    Object.prototype.hasOwnProperty.call(filterFields, 'normal') &&
    filterFields.normal !== undefined
  ) {
    const filterItems: ReportFilterItem[] = filterFields.normal;
    return (
      <NormalFilterForm
        filterItems={filterItems}
        filter={filter as Filter | null}
        setFilter={setFilter}
        stepperProps={stepperProps}
      />
    );
  } else if (
    Object.prototype.hasOwnProperty.call(filterFields, 'ship') &&
    filterFields.ship !== undefined
  ) {
    const filterItems: ReportFilterItem[] = filterFields.ship;
    return (
      <ShipFilterForm
        filterItems={filterItems}
        filter={filter as AutoCompleteShipInfo[] | null}
        setFilter={setFilter}
        stepperProps={stepperProps}
      ></ShipFilterForm>
    );
  } else if (
    Object.prototype.hasOwnProperty.call(filterFields, 'shiplist') &&
    filterFields.shiplist !== undefined
  ) {
    const filterItems: ReportFilterItem[] = filterFields.shiplist;
    return (
      <ShiplistFilterForm
        filterItems={filterItems}
        filter={filter as string | null}
        setFilter={setFilter}
        stepperProps={stepperProps}
      ></ShiplistFilterForm>
    );
  } else if (
    Object.prototype.hasOwnProperty.call(filterFields, 'combined') &&
    filterFields.combined !== undefined
  ) {
    const filterItems: ReportFilterItem[] = filterFields.combined;
    return (
      <CombinedFilterForm
        filterItems={filterItems}
        filter={
          filter as Record<
            string,
            Filter | AutoCompleteShipInfo[] | string | null
          >
        }
        setFilter={setFilter}
        stepperProps={stepperProps}
      ></CombinedFilterForm>
    );
  } else {
    return;
  }
};

interface TabbedContentProps {
  filterFields: FilterStructure;
  filter: ReportFilterType | null;
  setFilter: (filter: ReportFilterType | null) => void;
  stepperProps: useStepperReturn;
}

const updateTabBasedOnFilterFields = (
  filterFields: FilterStructure
): string => {
  if (filterFields.normal) {
    return 'normal';
  } else if (filterFields.ship) {
    return 'ship';
  } else {
    return 'shiplist';
  }
};

const TabbedContent = ({
  filterFields,
  filter,
  setFilter,
  stepperProps,
}: TabbedContentProps) => {
  const { messages } = useLocalization();
  const { filterTab, setFilterTab } = useCreateReportContext();

  // Initialize filterTab from filterFields if not already set
  const initialTab = updateTabBasedOnFilterFields(filterFields);
  if (!filterTab || filterTab !== initialTab) {
    // Only update if the current tab is not valid for this report type
    const validTabs = Object.keys(filterFields);
    if (!validTabs.includes(filterTab)) {
      setFilterTab(initialTab as FilterType);
    }
  }

  const tab = filterTab || initialTab;

  const normalFilterItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(filterFields, 'normal')) {
      return filterFields.normal;
    }
    return null;
  }, [filterFields]);

  const shipFilterItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(filterFields, 'ship')) {
      return filterFields.ship;
    }
    return null;
  }, [filterFields]);

  const shiplistFilterItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(filterFields, 'shiplist')) {
      return filterFields.shiplist;
    }
    return null; // or a default value
  }, [filterFields]);

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setFilterTab(value as FilterType)}
      defaultValue="normal"
      className="w-full space-y-4 pt-4 h-full flex flex-col"
    >
      <TabsList className="w-fit">
        {normalFilterItems && (
          <TabsTrigger value="normal">
            {messages.reportsCreateReportFilterTabNormal}
          </TabsTrigger>
        )}
        {shipFilterItems && (
          <TabsTrigger value="ship">
            {messages.reportsCreateReportFilterTabShip}
          </TabsTrigger>
        )}
        {shiplistFilterItems && (
          <TabsTrigger value="shiplist">
            {messages.reportsCreateReportFilterTabShiplist}
          </TabsTrigger>
        )}
      </TabsList>
      {normalFilterItems && tab === 'normal' && (
        <NormalFilterForm
          filterItems={normalFilterItems}
          filter={filter as Filter | null}
          setFilter={setFilter}
          stepperProps={stepperProps}
        />
      )}
      {shipFilterItems && tab === 'ship' && (
        <ShipFilterForm
          filterItems={shipFilterItems}
          filter={filter as AutoCompleteShipInfo[] | null}
          setFilter={setFilter}
          stepperProps={stepperProps}
        />
      )}
      {shiplistFilterItems && tab === 'shiplist' && (
        <ShiplistFilterForm
          filterItems={shiplistFilterItems}
          filter={filter as string | null}
          setFilter={setFilter}
          stepperProps={stepperProps}
        />
      )}
    </Tabs>
  );
};

export const FilterStep = () => {
  const { messages } = useLocalization();

  const { stepperProps, filter, setFilter, chosenReportType } =
    useCreateReportContext();

  const filterFields = useMemo(
    () => chosenReportType?.filter as FilterStructure,
    [chosenReportType]
  );
  return (
    <div className="grid grid-rows-[auto_1fr_auto] h-full">
      <StepperHeadline
        headerText={messages.reportsCreateReportFilterTitle}
        subHeaderText={messages.reportsCreateReportFilterSubtitle}
      ></StepperHeadline>
      {filterFields && Object.keys(filterFields).length === 1 && (
        <SingleContent
          filter={filter}
          setFilter={setFilter}
          stepperProps={stepperProps}
          filterFields={filterFields}
        ></SingleContent>
      )}
      {filterFields && Object.keys(filterFields).length > 1 && (
        <TabbedContent
          filter={filter}
          setFilter={setFilter}
          stepperProps={stepperProps}
          filterFields={filterFields}
        ></TabbedContent>
      )}
    </div>
  );
};
